<template>
  <div class="filters mb-5">
    <div class="row more-wrapper gy-3 gy-lg-0 mb-5">
      <div class="col-lg-4">
        <div class="input-search-wrapper">
          <label
            for="search-input"
            class="form-label visually-hidden"
            v-html="$store.state.i18n.label_search"
          ></label>

          <div class="input-group">
            <input
              type="text"
              class="form-control"
              id="search-input"
              :placeholder="$store.state.i18n.placeholder"
              @input="debounceSearch"
            />
            <span class="input-group-text"><i class="fal fa-search"></i></span>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <multiselect
          :options="$store.getters.getLocations"
          label="name"
          select-label=""
          selected-label=""
          deselect-label=""
          :placeholder="$store.state.i18n.in_a_location"
          id="location"
          @input="updateLocation"
          tag-placeholder=""
          v-model="currentLocation"
        >
          <template slot="singleLabel" slot-scope="props"
            ><span class="option__desc"
              ><span
                class="option__title"
                v-html="props.option.name"
              ></span></span
          ></template>

          <template slot="option" slot-scope="props"
            ><div class="option__desc">
              <span class="option__title" v-html="props.option.name">{{
                props.option.title
              }}</span>
            </div>
          </template>

          <template slot="noOptions">-</template>
          <template slot="noResult">-</template>
        </multiselect>
      </div>

      <div class="col-lg-4 text-center text-lg-end">
        <div class="wrapper-more-filter">
          <button
            class="btn btn-more-filter"
            type="button"
            @click="filtersAreOpen = !filtersAreOpen"
          >
            <svg
              class="picto"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 28.35 28.35"
            >
              <path d="M1.45,7.81H26.9" />
              <path d="M6,14.17H22.35" />
              <path d="M11.45,20.54H16.9" />
            </svg>
            {{ $store.state.i18n.more_filter }}
          </button>
        </div>
      </div>

      <div
        class="dropdown-menu"
        ref="dropdown"
        :class="{ open: filtersAreOpen }"
      >
        <div class="row">
          <div class="row">
            <div
              class="col-lg-4 col-xxl mb-4 mb-xxl-0"
              v-for="(taxonomy, index) in $store.state.taxonomies"
              :key="taxonomy.taxonomy"
            >
              <CheckboxFilter :tax="taxonomy" :tax_key="index" />
            </div>
          </div>

          <div
            class="col post-tags-list mt-4 mt-lg-0 mt-xxl-4"
            v-if="$store.state.tags_list.length"
          >
            <p class="filter-title mt-4" v-html="$store.state.i18n.topics"></p>
            <button
              v-for="tag in $store.getters.getTagsList"
              :key="tag.type_name + '_' + tag.term_id"
              v-html="'#' + tag.name"
              class="btn btn-post-tag"
              @click="tagClick(tag.name, tag.term_id)"
            ></button>
          </div>

          <show-at :breakpoints="{ medium: 992 }" breakpoint="mediumAndBelow">
            <button
              class="btn btn-more-filter"
              type="button"
              @click="filtersAreOpen = !filtersAreOpen"
            >
              <i class="fal fa-times me-2"></i>{{ $store.state.i18n.close }}
            </button>
          </show-at>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="wrapper-month-list">
          <button
            class="btn arrows"
            :class="prevArrowClass"
            @click="prevMonth()"
          >
            <i class="fal fa-angle-left"></i>
          </button>
          <button
            class="btn arrows"
            :class="nextArrowClass"
            @click="nextMonth()"
          >
            <i class="fal fa-angle-right"></i>
          </button>

          <multiselect
            :options="monthList"
            label="name"
            select-label=""
            selected-label=""
            deselect-label=""
            id="month"
            @input="updateMonth"
            tag-placeholder=""
            :value="currentMonth"
            :searchable="false"
            :allowEmpty="false"
          >
            <template slot="noOptions">-</template>
            <template slot="noResult">-</template>
          </multiselect>
        </div>
      </div>
    </div>

    <div ref="spacer"></div>

    <div class="tags-list mt-4">
      <TagBadge
        v-for="tag in $store.state.filterTags"
        :key="tag.type_name + '_' + tag.term_id"
        :tag="tag"
      />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Vuex from "vuex";
import TagBadge from "../../../hub/src/components/parts/TagBadge";
import gsap from "../../../node_modules/gsap/dist/gsap.js";
import { showAt, hideAt } from "vue-breakpoints";
import Multiselect from "vue-multiselect";
import CheckboxFilter from "../../../hub/src/components/parts/CheckboxFilter";

const { mapActions, mapState, mapGetters } = Vuex;

Vue.use(Vuex);

export default {
  name: "Filters",
  components: {
    TagBadge,
    showAt,
    hideAt,
    Multiselect,
    CheckboxFilter,
  },

  data() {
    return {
      filtersAreOpen: false,
      debounce: null,
      currentLocation: null,
    };
  },

  computed: {
    ...mapState(["monthList", "currentMonth"]),

    nextArrowClass: function () {
      return {
        disabled: this.$store.getters.getCurrentPositionInMonth == 0,
      };
    },

    prevArrowClass: function () {
      return {
        disabled:
          this.$store.getters.getCurrentPositionInMonth ==
          this.$store.state.monthList.length - 1,
      };
    },
  },

  watch: {
    filtersAreOpen: function (val, oldVal) {
      let spacerHeight = 0;

      if (val) {
        spacerHeight = this.$refs.dropdown.offsetHeight;
      }

      gsap.to(this.$refs.spacer, {
        height: spacerHeight,
        duration: 0.25,
        onComplete: function () {
          window.dispatchEvent(new Event("resize"));
        },
      });
    },
  },

  methods: {
    debounceSearch(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.$store.commit("UPDATE_SEARCH", event.target.value);
      }, 1000);
    },

    tagClick(tagName, tag) {
      this.$store.commit("ADD_TAG", [tagName, tag]);
    },

    updateLocation(value) {
      if (value) {
        value = value.term_id;
      }

      this.$store.commit("UPDATE_LOCATION", value);

      this.$store.dispatch("newRequest");
    },

    updateMonth(value) {
      this.$store.commit("UPDATE_MONTH", value);
    },

    nextMonth() {
      let currentIndex = this.$store.getters.getCurrentPositionInMonth;

      if (currentIndex > 0) {
        this.$store.commit(
          "UPDATE_MONTH",
          this.$store.state.monthList[currentIndex - 1]
        );
      }
    },

    prevMonth() {
      let currentIndex = this.$store.getters.getCurrentPositionInMonth;

      if (currentIndex < this.$store.state.monthList.length - 1) {
        this.$store.commit(
          "UPDATE_MONTH",
          this.$store.state.monthList[currentIndex + 1]
        );
      }
    },
  },
};
</script>
